import React, { useMemo } from 'react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, defs, linearGradient, stop,
} from 'recharts';

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background  : 'rgba(255,255,255,0.95)',
      border      : '1px solid #e0e7ff',
      borderRadius: 10,
      padding     : '8px 12px',
      boxShadow   : '0 4px 16px rgba(99,102,241,0.15)',
      fontSize    : 12,
      fontWeight  : 600,
      color       : '#4f46e5',
    }}>
      {payload[0].value.toFixed(1)} ADC
    </div>
  );
};

export default function EcgChart({ data }) {
  // Calculate real sample rate and time window from timestamps
  const sampleRate = useMemo(() => {
    if (data.length < 10) return null;
    const dt = data[data.length - 1].t - data[0].t;
    if (dt <= 0) return null;
    return Math.round((data.length - 1) / (dt / 1000));
  }, [data]);

  const timeWindow = useMemo(() => {
    if (data.length < 2) return null;
    const dt = data[data.length - 1].t - data[0].t;
    return (dt / 1000).toFixed(1);
  }, [data]);

  // Map to time-relative X axis (seconds from oldest sample)
  const displayData = useMemo(() => {
    if (data.length === 0) return [];
    const t0 = data[0].t;
    return data.map(p => ({ t: ((p.t - t0) / 1000).toFixed(2), v: p.v }));
  }, [data]);

  const hasData = displayData.length > 0;

  return (
    <div style={s.card}>
      {/* Card Header */}
      <div style={s.header}>
        <div style={s.titleRow}>
          <div style={s.iconWrap}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M3 12h3l2-7 4 14 3-10 2 3h4" stroke="#6366f1" strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <span style={s.title}>ECG Waveform</span>
        </div>
        <div style={s.badges}>
          <span style={s.badge}>{sampleRate ? `${sampleRate} Hz` : '50 Hz'}</span>
          <span style={{ ...s.badge, ...s.badgeLive, animation: hasData ? 'pulse-ring 2s infinite' : 'none' }}>
            {hasData ? '● LIVE' : '○ WAITING'}
          </span>
        </div>
      </div>

      {/* Chart */}
      <div style={s.chartWrap}>
        {!hasData && (
          <div style={s.placeholder}>
            <div style={s.placeholderIcon}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M3 12h3l2-7 4 14 3-10 2 3h4" stroke="#c7d2fe" strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span style={s.placeholderText}>Awaiting ECG signal from patch…</span>
          </div>
        )}
        {hasData && (
          <ResponsiveContainer width="100%" height={190}>
            <AreaChart data={displayData} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
              <defs>
                <linearGradient id="ecgGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#6366f1" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0.01} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="4 4" stroke="#e0e7ff" strokeOpacity={0.8} />
              <XAxis
                dataKey="t"
                type="number"
                domain={['auto', 'auto']}
                tickCount={6}
                tickFormatter={v => `${Number(v).toFixed(1)}s`}
                tick={{ fill: '#9ca3af', fontSize: 9, fontFamily: 'Inter' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                domain={['auto', 'auto']}
                tick={{ fill: '#9ca3af', fontSize: 10, fontFamily: 'Inter' }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="v"
                stroke="#6366f1"
                strokeWidth={2}
                fill="url(#ecgGradient)"
                dot={false}
                isAnimationActive={false}
                style={{ filter: 'drop-shadow(0 0 4px rgba(99,102,241,0.5))' }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Stats strip */}
      <div style={s.statsRow}>
        {[
          { label: 'Samples',     val: data.length },
          { label: 'Time Window', val: timeWindow ? `${timeWindow} s` : '—' },
          { label: 'Sample Rate', val: sampleRate ? `${sampleRate} Hz` : '50 Hz' },
          { label: 'Lead',        val: 'Single' },
        ].map(({ label, val }) => (
          <div key={label} style={s.stat}>
            <span style={s.statVal}>{val}</span>
            <span style={s.statLabel}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const s = {
  card: {
    background      : 'rgba(255,255,255,0.72)',
    backdropFilter  : 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderRadius    : 20,
    border          : '1px solid rgba(255,255,255,0.9)',
    boxShadow       : '0 8px 32px rgba(99,102,241,0.08), 0 1px 0 rgba(255,255,255,0.9) inset',
    padding         : '20px 22px 16px',
    overflow        : 'hidden',
  },
  header : { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
  titleRow: { display: 'flex', alignItems: 'center', gap: 10 },
  iconWrap: {
    width       : 32,
    height      : 32,
    borderRadius: 8,
    background  : 'linear-gradient(135deg, #eef2ff, #e0e7ff)',
    border      : '1px solid #c7d2fe',
    display     : 'flex',
    alignItems  : 'center',
    justifyContent: 'center',
  },
  title  : { fontSize: 15, fontWeight: 700, color: '#1e1b4b' },
  badges : { display: 'flex', gap: 8 },
  badge  : {
    fontSize    : 11,
    fontWeight  : 600,
    padding     : '4px 10px',
    borderRadius: 99,
    background  : '#f3f4f6',
    color       : '#6b7280',
    border      : '1px solid #e5e7eb',
  },
  badgeLive: {
    background  : '#ecfdf5',
    color       : '#059669',
    border      : '1px solid #a7f3d0',
  },
  chartWrap: { position: 'relative', minHeight: 190 },
  placeholder: {
    position      : 'absolute',
    inset         : 0,
    display       : 'flex',
    flexDirection : 'column',
    alignItems    : 'center',
    justifyContent: 'center',
    gap           : 10,
    background    : 'linear-gradient(135deg, #f8f9ff, #f0f4ff)',
    borderRadius  : 12,
    border        : '1.5px dashed #c7d2fe',
  },
  placeholderIcon: { opacity: 0.5 },
  placeholderText: { fontSize: 13, color: '#a5b4fc', fontWeight: 500 },
  statsRow: {
    display      : 'flex',
    gap          : 0,
    marginTop    : 14,
    borderTop    : '1px solid #f0f0ff',
    paddingTop   : 12,
    justifyContent: 'space-around',
  },
  stat     : { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 },
  statVal  : { fontSize: 13, fontWeight: 700, color: '#4f46e5' },
  statLabel: { fontSize: 10, color: '#9ca3af', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' },
};
